import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import SmartImage from '../SmartImage';
import { StatisticsRow } from '../../types/supplierStatistics';
import { money, number, percent } from './StatisticsTable';

type Size = NonNullable<StatisticsRow['sizes']>[number];
const sizeLabel = (size: Size) =>
  size.attribute2Code || size.attribute2Number || 'Saknad storlek';

export const colorLabel = (color: StatisticsRow) =>
  color.colorName ||
  color.attribute1Code ||
  color.attribute1Number ||
  'Saknad färgkoppling';

/** Availability is the sellable balance; physical and reserved stock remain inspectable. */
export default function StatisticsSizeStock({
  color,
}: {
  color: StatisticsRow;
}) {
  const sizes = color.sizes || [];
  const availableSizes = sizes.filter(
    (size) => size.availableBalance != null && size.availableBalance > 0,
  ).length;
  const unknownSizes = sizes.filter(
    (size) => size.availableBalance == null,
  ).length;
  const label = colorLabel(color);
  return (
    <Paper
      component="section"
      aria-label={`Lager för ${label}`}
      variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2.5 }}
    >
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 52,
            height: 60,
            flexShrink: 0,
            bgcolor: '#f8fafc',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <SmartImage
            src={color.imageUrl}
            alt={label}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h3" variant="subtitle1" fontWeight={700}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sizes.length
              ? `${availableSizes} av ${sizes.length} storlekar i lager`
              : 'Storleksunderlag saknas'}
            {unknownSizes > 0 && ` · ${unknownSizes} med okänt saldo`}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="h6" fontWeight={700}>
            {color.stockKnown ? `${number(color.availableStock)} st` : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tillgängligt
          </Typography>
        </Box>
      </Stack>
      {sizes.length ? (
        <Box
          component="ul"
          aria-label={`Tillgängligt per storlek, ${label}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
            gap: 1,
            p: 0,
            m: 0,
            listStyle: 'none',
          }}
        >
          {sizes.map((size, index) => {
            const balance = size.availableBalance;
            const state =
              balance == null
                ? 'Okänt'
                : balance > 0
                  ? 'I lager'
                  : balance === 0
                    ? 'Slut'
                    : 'Negativt';
            const inStock = balance != null && balance > 0;
            const negative = balance != null && balance < 0;
            return (
              <Box
                component="li"
                key={`${size.attribute2Code}-${size.attribute2Number}-${index}`}
                aria-label={`Storlek ${sizeLabel(size)}: ${balance == null ? 'okänt saldo' : `${number(balance)} st tillgängligt, ${state.toLowerCase()}`}`}
                sx={{
                  textAlign: 'center',
                  py: 1.25,
                  px: 0.5,
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: inStock
                    ? '#b7ddd3'
                    : negative
                      ? '#f3c591'
                      : 'divider',
                  bgcolor: inStock
                    ? '#eff8f4'
                    : negative
                      ? '#fff6ea'
                      : '#f7f8fa',
                  color: inStock
                    ? '#16634e'
                    : negative
                      ? '#924d0b'
                      : 'text.secondary',
                }}
              >
                <Typography
                  fontWeight={700}
                  sx={{ color: 'text.primary', overflowWrap: 'anywhere' }}
                >
                  {sizeLabel(size)}
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ mt: 0.25 }}>
                  {balance == null ? '—' : `${number(balance)} st`}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: 10, display: 'block', mt: 0.25 }}
                >
                  {state}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Aktuella storleks- eller lagerkopplingar saknas.
        </Typography>
      )}
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          mt: 1.5,
          '&:before': { display: 'none' },
          bgcolor: 'transparent',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore fontSize="small" />}
          sx={{
            p: 0,
            minHeight: 36,
            '& .MuiAccordionSummary-content': { my: 0.5 },
          }}
        >
          <Typography variant="body2" color="primary.main">
            Lagerdetaljer och priser · {label}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pb: 0 }}>
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip
              variant="outlined"
              size="small"
              label={`Inpris ex moms: ${money(color.costPrice)}`}
            />
            <Chip
              variant="outlined"
              size="small"
              label={`Utpris inkl moms: ${money(color.sellingPrice)}`}
            />
            <Chip
              variant="outlined"
              size="small"
              label={`Kalkylmarginal: ${percent(color.calculationMarginPercent)}`}
            />
          </Stack>
          {!!sizes.length && (
            <TableContainer
              tabIndex={0}
              role="region"
              aria-label={`Lagerdetaljer för ${label}`}
            >
              <Table
                size="small"
                aria-label={`Lagersaldon per storlek, ${label}`}
                sx={{
                  '& th': { color: 'text.secondary', bgcolor: '#f8fafc' },
                  '& tr:last-child td, & tr:last-child th': { borderBottom: 0 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Storlek</TableCell>
                    <TableCell align="right">Fysiskt</TableCell>
                    <TableCell align="right">Reserverat</TableCell>
                    <TableCell align="right">Tillgängligt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sizes.map((size, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {sizeLabel(size)}
                      </TableCell>
                      <TableCell align="right">
                        {number(size.physicalBalance)}
                      </TableCell>
                      <TableCell align="right">
                        {number(size.reservedBalance)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {number(size.availableBalance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
